import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, Save, ArrowRight, ClipboardCheck, 
  User, Activity, MessageSquare, AlertCircle
} from 'lucide-react';
import TamkeenLogo from '../components/TamkeenLogo';

// 1. استيراد سياق المصادقة لجلب هوية المؤطر
import { useAuth } from '../context/AuthContext';

// استيراد أدوات السحابة لقاعدة البيانات
import { db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'; // إضافة serverTimestamp

const EvaluationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // 2. استخراج بيانات المؤطر الحالي
  
  const [sessionData, setSessionData] = useState(null);
  const [evaluations, setEvaluations] = useState({});
  const [hoveredStar, setHoveredStar] = useState({ id: null, value: 0 });

  useEffect(() => {
    // 3. حماية الصفحة: طرد أي مستخدم يحاول الوصول للصفحة دون تسجيل دخول أمني
    if (!user) {
      navigate('/login');
      return;
    }

    const activeSession = localStorage.getItem('tamkeen_active_session');
    
    if (activeSession) {
      const parsedSession = JSON.parse(activeSession);
      setSessionData(parsedSession);
      
      const initialEvals = {};
      parsedSession.beneficiaries.forEach(b => {
        initialEvals[b.id] = { rating: 0, note: '' }; 
      });
      setEvaluations(initialEvals);
    } else {
      navigate('/workshop');
    }
  }, [navigate, user]);

  const handleRating = (beneficiaryId, value) => {
    setEvaluations(prev => ({
      ...prev,
      [beneficiaryId]: { ...prev[beneficiaryId], rating: value }
    }));
  };

  const handleNoteChange = (beneficiaryId, text) => {
    setEvaluations(prev => ({
      ...prev,
      [beneficiaryId]: { ...prev[beneficiaryId], note: text }
    }));
  };

  const handleSaveEvaluations = async () => {
    const isComplete = Object.values(evaluations).every(e => e.rating > 0);
    
    if (!isComplete) {
      alert("يرجى تقييم جميع المستفيدين (نجمة واحدة على الأقل) قبل الحفظ.");
      return;
    }

    const sessionId = Date.now().toString();
    const finalReport = {
      sessionId: sessionId,
      activityId: sessionData.activityId || 'unknown_activity',
      activityTitle: sessionData.activityTitle || 'بدون عنوان',
      groupName: sessionData.groupName || 'مجموعة غير محددة',
      workshop: sessionData.workshop || 'ورشة غير محددة',
      
      // 4. [التدخل الجراحي]: تأمين الهوية لمنع خطأ Undefined في Firebase
      educatorId: user?.uid || user?.id || 'unknown_educator',
      educatorName: user?.displayName || user?.name || 'مؤطر تمكين',
      
      // 5. استخدام التوقيت السحابي الموثوق
      createdAt: serverTimestamp(), 
      
      results: evaluations
    };
    
    try {
      await setDoc(doc(db, 'evaluations_history', sessionId), finalReport);

      localStorage.removeItem('tamkeen_active_session');

      alert("تم حفظ التقييمات بنجاح في سجل الورشة السحابي!");
      navigate('/workshop');
    } catch (error) {
      console.error("Evaluation Save Error:", error);
      alert("حدث خطأ أثناء الاتصال بالسحابة. المرجو التحقق من الإنترنت وإعادة المحاولة.");
    }
  };

  if (!sessionData) return null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans direction-rtl flex flex-col">
      <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between shadow-sm z-10 sticky top-0">
        <div className="flex items-center gap-4">
          <TamkeenLogo className="w-10 h-10" />
          <div>
            <h1 className="text-xl font-extrabold text-slate-800">الشبكة التحليلية والتقييم</h1>
            <p className="text-xs font-bold text-slate-500 mt-1">توثيق الأداء البيداغوجي للمستفيدين</p>
          </div>
        </div>
        <button 
          onClick={() => navigate('/workshop')} 
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold text-sm bg-slate-100 hover:bg-slate-200 px-4 py-2 rounded-xl transition-colors"
        >
          إلغاء التقييم والعودة <ArrowRight className="w-4 h-4" />
        </button>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full p-6 md:p-10">
        
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shrink-0">
              <ClipboardCheck className="w-7 h-7" />
            </div>
            <div>
              <p className="text-sm font-bold text-indigo-600 mb-1">النشاط المنجز:</p>
              <h2 className="text-xl font-extrabold text-slate-800">{sessionData.activityTitle}</h2>
            </div>
          </div>
          
          <div className="flex gap-6 border-t md:border-t-0 md:border-r border-slate-100 pt-4 md:pt-0 md:pr-6 w-full md:w-auto">
            <div>
              <p className="text-xs text-slate-500 font-bold mb-1">المجموعة المستهدفة:</p>
              <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><User className="w-4 h-4 text-slate-400"/> {sessionData.groupName}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-bold mb-1">تاريخ التنفيذ:</p>
              <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><Activity className="w-4 h-4 text-slate-400"/> {new Date(sessionData.timestamp).toLocaleDateString('en-GB')}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 text-blue-800 p-4 rounded-2xl flex items-start gap-3 mb-8">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm font-medium leading-relaxed">
            <strong className="font-bold">ملاحظة للمؤطر:</strong> قم بتقييم مستوى استجابة وتفاعل كل مستفيد بناءً على الأهداف المحددة للنشاط. استخدم النجوم للتقييم الكمي، وحقل الملاحظات لأي تفاصيل نوعية إضافية.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden mb-10">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
              <tr>
                <th className="p-5 font-bold w-1/4">اسم المستفيد</th>
                <th className="p-5 font-bold text-center w-1/3">مستوى الاستجابة (1 - 5)</th>
                <th className="p-5 font-bold w-5/12">ملاحظات المؤطر (اختياري)</th>
              </tr>
            </thead>
            <tbody>
              {sessionData.beneficiaries.map((b) => (
                <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="p-5">
                    <p className="font-bold text-slate-900 text-base">{b.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{b.condition}</p>
                  </td>
                  
                  <td className="p-5 text-center">
                    <div className="flex items-center justify-center gap-1.5 direction-ltr">
                      {[1, 2, 3, 4, 5].map((starValue) => {
                        const currentRating = evaluations[b.id]?.rating || 0;
                        const isHovered = hoveredStar.id === b.id && starValue <= hoveredStar.value;
                        const isFilled = starValue <= currentRating;
                        
                        return (
                          <button
                            key={starValue}
                            type="button"
                            onClick={() => handleRating(b.id, starValue)}
                            onMouseEnter={() => setHoveredStar({ id: b.id, value: starValue })}
                            onMouseLeave={() => setHoveredStar({ id: null, value: 0 })}
                            className="p-1 transition-transform hover:scale-110 focus:outline-none"
                          >
                            <Star 
                              className={`w-8 h-8 ${isHovered || isFilled ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'fill-transparent text-slate-300'}`} 
                              strokeWidth={1.5}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  
                  <td className="p-5">
                    <div className="relative">
                      <MessageSquare className="absolute right-3 top-3 w-4 h-4 text-slate-400" />
                      <textarea 
                        placeholder="أضف ملاحظاتك حول تفاعل المستفيد..."
                        value={evaluations[b.id]?.note || ''}
                        onChange={(e) => handleNoteChange(b.id, e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pr-10 pl-4 py-2.5 text-sm text-slate-800 focus:border-indigo-500 outline-none resize-none h-12 focus:h-24 transition-all"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <motion.button 
            whileHover={{ y: -2 }}
            onClick={handleSaveEvaluations}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-xl shadow-indigo-600/20 flex items-center gap-3 transition-all"
          >
            <Save className="w-6 h-6" /> حفظ السجل التقييمي
          </motion.button>
        </div>

      </main>
    </div>
  );
};

export default EvaluationPage;